import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ListTodo, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { da, enUS } from 'date-fns/locale';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, ListRow } from '@/components/ui/Card';
import { StatusChip } from '@/components/ui/Chip';
import { Skeleton } from '@/components/ui/Skeleton';
import { TaskFormSlideOver } from '@/components/tasks/TaskFormSlideOver';
import { useAuth } from '@/lib/auth';
import { useToggleStaffTaskDone, useStaffTasksForEntity } from '@/hooks/useStaffTasks';
import {
  staffTaskIsOverdue,
  type StaffTask,
  type StaffTaskEntityType,
  type StaffTaskLink,
} from '@/lib/staffTasks';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

function statusTone(status: StaffTask['status']) {
  switch (status) {
    case 'open':
      return 'upcoming' as const;
    case 'in_progress':
      return 'draft' as const;
    case 'done':
      return 'completed' as const;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function EntityTasksPanel({
  clinicId,
  entityType,
  entityId,
}: {
  clinicId: string;
  entityType: StaffTaskEntityType;
  entityId: string;
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const canView = hasPermission(clinicId, 'tasks', 'view');
  const canCreate = hasPermission(clinicId, 'tasks', 'create');
  const query = useStaffTasksForEntity(canView ? clinicId : null, entityType, entityId);
  const toggleDone = useToggleStaffTaskDone();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<StaffTask | null>(null);
  const dateLocale = i18n.language === 'da' ? da : enUS;
  const presetLinks: StaffTaskLink[] = [{ entity_type: entityType, entity_id: entityId }];

  if (!canView) return null;

  const tasks = query.data ?? [];

  return (
    <>
      <Card>
        <CardHeader
          title={t('tasks.title')}
          count={tasks.length > 0 ? tasks.length : undefined}
          action={
            canCreate ? (
              <Button type="button" variant="secondary" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                {t('tasks.create')}
              </Button>
            ) : null
          }
        />
        {query.isLoading ? (
          <div className="mt-s3 space-y-s2" aria-busy>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : tasks.length === 0 ? (
          <p className={cn(TYPE.small, 'mt-s3 flex items-center gap-s2 text-ds-muted')}>
            <ListTodo className="h-4 w-4" aria-hidden />
            {t('tasks.empty_related')}
          </p>
        ) : (
          <div className="mt-s3">
            {tasks.slice(0, 6).map((task) => {
              const overdue = staffTaskIsOverdue(task.deadline_date, task.deadline_time, task.status);
              return (
                <ListRow
                  key={task.id}
                  rail={task.status === 'done' ? 'finalized' : overdue ? 'cancelled' : 'draft'}
                  className="hover:bg-ds-sunk"
                >
                  <button
                    type="button"
                    className="shrink-0 text-ds-teal disabled:opacity-50"
                    disabled={toggleDone.isPending}
                    aria-label={
                      task.status === 'done' ? t('tasks.mark_open') : t('tasks.mark_done')
                    }
                    onClick={() => {
                      void toggleDone
                        .mutateAsync({
                          task_id: task.id,
                          clinic_id: clinicId,
                          done: task.status !== 'done',
                        })
                        .then(() =>
                          toast.success(
                            task.status === 'done'
                              ? t('tasks.toast_reopened')
                              : t('tasks.toast_completed'),
                          ),
                        )
                        .catch(() => toast.error(t('tasks.error_update')));
                    }}
                  >
                    <CheckCircle
                      className={cn(
                        'h-5 w-5',
                        task.status === 'done' ? 'text-ds-green-ink' : 'text-ds-line',
                      )}
                    />
                  </button>
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => setEditing(task)}
                  >
                    <p
                      className={cn(
                        TYPE.body,
                        'text-ds-text',
                        task.status === 'done' && 'text-ds-muted line-through',
                      )}
                    >
                      {task.title}
                    </p>
                    <p
                      className={cn(
                        TYPE.small,
                        overdue ? 'text-ds-danger' : 'text-ds-muted',
                      )}
                    >
                      {task.deadline_date
                        ? format(
                            new Date(`${task.deadline_date}T00:00:00`),
                            'd. MMM yyyy',
                            { locale: dateLocale },
                          )
                        : t('tasks.no_deadline')}
                      {overdue ? ` · ${t('tasks.overdue')}` : ''}
                    </p>
                  </button>
                  <StatusChip tone={statusTone(task.status)}>
                    {t(`tasks.status.${task.status}`)}
                  </StatusChip>
                </ListRow>
              );
            })}
          </div>
        )}
      </Card>
      {creating ? (
        <TaskFormSlideOver
          clinicId={clinicId}
          presetLinks={presetLinks}
          onClose={() => setCreating(false)}
        />
      ) : null}
      {editing ? (
        <TaskFormSlideOver
          clinicId={clinicId}
          task={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}
